import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { QuoteRequestDialog } from "@/components/common/QuoteRequestDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Compass, Star, Users, Clock, ArrowRight, Check,
  Ship, Mountain, Snowflake, Camera, Binoculars, Award
} from "lucide-react";
import { expeditions } from "@/data/cruises";

const CDN = "https://res.cloudinary.com/enchanting";
const img  = (path, w = 900, h = 560) =>
  `${CDN}/q_82,f_auto,w_${w},h_${h},c_fill/${path}`;

const IMAGES = {
  hero:      img("quark-web/2024/10/homepagehero.jpeg", 1600, 800),
  hikers:    img("quark-web/2024/09/EXP2620-20pax_hike_portal_point_acaciajohnson_1.jpg", 900, 560),
  mountain:  img("quark-web/2019/08/ANT_Mountain.jpeg", 900, 560),
  portrait:  img("quark-web/2024/09/bestof-davidmerronimg-1498.jpg", 900, 560),
  best2:     img("quark-web/2024/09/Bestof-DavidMerronIMG_9059-min.jpg", 900, 560),
  landscape: img("quark-web/2024/10/Landscape-Image-Containers-11.jpg", 900, 560),
  crimmin:   img("quark-web/2024/10/20150606-IMG_8547-Edit_Samantha-Crimmin.jpg", 900, 560),
  ship:      img("quark-web/2017/06/WorldExplorer_Exterior_image00004_Credit-LauritzSchonfeld.jpg", 900, 560),
};

const antarcticExpeditions = expeditions.filter(e => e.region === "Antarctica");

const WHY = [
  {
    icon: Snowflake,
    title: "The Last Great Wilderness",
    body: "Antarctica is the most pristine place on Earth — no permanent human settlement, no pollution, and wildlife that has never learned to fear people. Every shore landing feels like stepping onto a planet no one else has visited.",
  },
  {
    icon: Binoculars,
    title: "Extraordinary Wildlife",
    body: "Millions of penguins, humpback and minke whales breaching beside Zodiacs, leopard seals resting on ice floes, and albatrosses with 11-foot wingspans gliding overhead. Antarctica delivers wildlife encounters that simply don't exist anywhere else.",
  },
  {
    icon: Award,
    title: "Exclusive Partnership",
    body: "Tripsyfy is an authorised booking partner of Quark Expeditions — the world's polar expedition pioneer since 1991. We handle everything from your first call to your return flight, at the same price you'd pay direct.",
  },
];

const STATS = [
  { value: "30+", label: "Years of polar expertise" },
  { value: "1991", label: "Quark founded" },
  { value: "199", label: "Max guests per voyage" },
  { value: "100%", label: "Polar-trained crew" },
];

const GALLERY = [
  { src: IMAGES.hikers,    alt: "Hikers at Portal Point, Antarctic Peninsula" },
  { src: IMAGES.mountain,  alt: "Ice mountains and glaciers, Antarctica" },
  { src: IMAGES.best2,     alt: "Wildlife encounter, Antarctica" },
  { src: IMAGES.landscape, alt: "Antarctic landscape at dusk" },
  { src: IMAGES.crimmin,   alt: "Zodiac expedition landing, Antarctica" },
  { src: IMAGES.ship,      alt: "World Explorer expedition ship" },
];

const INCLUDED = [
  "All Zodiac landings & shore excursions",
  "Expert naturalist & glaciologist team",
  "All meals & open bar onboard",
  "Polar-grade expedition parka (yours to keep)",
  "Rubber boots for landings (loan)",
  "Wi-Fi onboard",
  "Port taxes & fuel surcharges",
  "Emergency evacuation insurance",
];

export default function AntarcticaPage() {
  const [quoteOpen,  setQuoteOpen]  = useState(false);
  const [quoteData,  setQuoteData]  = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const openQuote = (exp) => {
    setQuoteData({
      type:        "cruise",
      cruiseName:  exp.title,
      destination: exp.region,
      duration:    exp.duration,
      ship:        exp.ship,
    });
    setQuoteOpen(true);
  };

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <SeoHead
        title="Antarctica Expeditions"
        description="Book Antarctica expedition cruises with Tripsyfy. Quark Expeditions partnership — Zodiac landings, penguin encounters, and polar adventures."
        canonical="https://tripsyfy.com/expeditions/antarctica"
      />
      <Navbar />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative h-[70vh] min-h-[500px] flex items-end overflow-hidden">
        <img
          src={IMAGES.hero}
          alt="Antarctic Peninsula icebergs and glaciers"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(9,14,76,.35) 0%, rgba(9,14,76,.25) 40%, rgba(9,14,76,.75) 100%)" }} />
        <div className="relative z-10 container-wide px-4 sm:px-6 lg:px-8 pb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-none border border-white/30 text-white text-xs font-semibold px-3 py-1.5 rounded-pill">
              <Compass className="h-3.5 w-3.5" /> Polar Expeditions
            </span>
            <span className="text-white/60 text-xs">In partnership with</span>
            <span className="text-white font-bold text-sm tracking-wide">Quark Expeditions®</span>
          </div>
          <h1 className="heading-xl text-white mb-4 max-w-3xl [text-wrap:balance]">
            Antarctica — The Last Untouched Continent
          </h1>
          <p className="text-white/80 text-base max-w-xl leading-relaxed mb-8">
            Set foot on the 7th continent. Zodiac landings among penguin colonies,
            breaching humpbacks, and ancient blue ice — with the world's polar expedition pioneers since 1991.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              size="lg"
              className="bg-accent text-white hover:bg-accent/90 gap-2"
              onClick={() => {
                setQuoteData({ type: "cruise", destination: "Antarctica", cruiseName: "Antarctica Expedition" });
                setQuoteOpen(true);
              }}
            >
              Request a Quote <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-white/10 border-white/40 text-white hover:bg-white/20"
              asChild
            >
              <a href="#expeditions">View Expeditions</a>
            </Button>
          </div>
        </div>
      </section>

      {/* ── Stats strip ──────────────────────────────────────────────── */}
      <section className="bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-2xl font-bold text-accent">{s.value}</p>
                <p className="text-xs text-white/60 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Antarctica ───────────────────────────────────────────── */}
      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <p className="overline mb-2">Why go</p>
            <h2 className="heading-lg text-primary mb-3 max-w-xl">
              The world's most extraordinary journey
            </h2>
            <p className="text-muted-foreground max-w-2xl mb-12 leading-relaxed">
              Antarctica isn't a cruise destination — it's an expedition. No five-star resorts,
              no crowds, no itinerary you've seen before. Just raw, staggering wilderness and
              wildlife encounters that change the way you see the planet.
            </p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {WHY.map((w, i) => (
              <AnimatedSection key={w.title} delay={i * 0.1}>
                <div className="bg-white rounded-xl p-6 shadow-card h-full">
                  <div className="h-11 w-11 rounded-xl bg-surface-soft flex items-center justify-center mb-4">
                    <w.icon className="h-5 w-5 text-accent" />
                  </div>
                  <h3 className="font-heading font-bold text-primary text-lg mb-3">{w.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{w.body}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Photo gallery ────────────────────────────────────────────── */}
      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <p className="overline mb-2">The experience</p>
            <h2 className="heading-lg text-primary mb-10">Life on expedition</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {GALLERY.map((g, i) => (
              <AnimatedSection key={i} delay={i * 0.05} className={i === 0 ? "md:col-span-2 md:row-span-1" : ""}>
                <div className="rounded-xl overflow-hidden aspect-[4/3] group">
                  <img
                    src={g.src}
                    alt={g.alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
              </AnimatedSection>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center mt-4">
            Photos courtesy of Quark Expeditions® — Polar-Obsessed Since 1991
          </p>
        </div>
      </section>

      {/* ── Expedition packages ──────────────────────────────────────── */}
      <section id="expeditions" className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-2">Expedition packages</p>
            <h2 className="heading-lg text-white mb-3">Choose your Antarctic voyage</h2>
            <p className="text-white/60 text-sm max-w-xl leading-relaxed mb-10">
              Every departure departs from Ushuaia, Argentina — the world's southernmost city.
              All-inclusive pricing: no hidden costs, no single supplements on select departures.
            </p>
          </AnimatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {antarcticExpeditions.map((exp, i) => (
              <AnimatedSection key={exp.id} delay={i * 0.1}>
                <div className="bg-white rounded-xl shadow-card-lift overflow-hidden flex flex-col h-full group">
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={i === 0 ? IMAGES.hikers : IMAGES.portrait}
                      alt={exp.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <Badge variant="accent" className="absolute top-3 left-3">{exp.duration}</Badge>
                    {exp.badge === "Exclusive" && (
                      <span className="absolute top-3 right-3 bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                        Exclusive Access
                      </span>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Compass className="h-4 w-4 text-accent shrink-0" />
                      <span className="text-xs text-muted-foreground font-medium">{exp.ship}</span>
                    </div>
                    <h3 className="font-heading font-bold text-primary text-xl mb-2 leading-snug">{exp.title}</h3>
                    <div className="flex items-center gap-1.5 mb-3">
                      <Star className="h-3.5 w-3.5 fill-accent text-accent" />
                      <span className="text-sm font-semibold text-primary">{exp.rating}</span>
                      <span className="text-xs text-muted-foreground">({exp.reviews} reviews)</span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">{exp.description}</p>
                    <div className="mb-5">
                      <p className="text-xs font-semibold text-primary mb-2">Expedition highlights</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {exp.highlights.slice(0, 4).map((h, j) => (
                          <span key={j} className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Check className="h-3 w-3 text-accent shrink-0" /> {h}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-border/60 mt-auto">
                      <div>
                        <p className="text-xs text-muted-foreground">Starting from</p>
                        <p className="text-2xl font-bold text-primary">${exp.startingPrice.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">per person</p>
                      </div>
                      <Button
                        onClick={() => openQuote(exp)}
                        className="bg-accent text-white hover:bg-accent/90 gap-2"
                      >
                        Request Quote <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <AnimatedSection delay={0.2}>
            <div className="mt-8 text-center">
              <p className="text-white/60 text-sm mb-4">Looking for Arctic, Greenland, or South Georgia expeditions?</p>
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" asChild>
                <Link to="/cruises">View all polar expeditions →</Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── What's included ──────────────────────────────────────────── */}
      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <p className="overline mb-2">All-inclusive</p>
              <h2 className="heading-lg text-primary mb-4">Everything is taken care of</h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Every Quark expedition is fully all-inclusive. From the moment you board in Ushuaia
                to the last Zodiac back to the ship, there is nothing extra to pay.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {INCLUDED.map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <div className="h-5 w-5 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-accent" />
                    </div>
                    <span className="text-sm text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              <Button
                size="lg"
                className="mt-8 bg-accent text-white hover:bg-accent/90 gap-2"
                onClick={() => {
                  setQuoteData({ type: "cruise", destination: "Antarctica", cruiseName: "Antarctica Expedition" });
                  setQuoteOpen(true);
                }}
              >
                Get a Personalised Quote <ArrowRight className="h-4 w-4" />
              </Button>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl overflow-hidden aspect-[3/4]">
                  <img src={IMAGES.mountain} alt="Antarctic glaciers" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="rounded-xl overflow-hidden aspect-[3/4] mt-8">
                  <img src={IMAGES.crimmin} alt="Zodiac expedition landing" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ── Partner trust bar ────────────────────────────────────────── */}
      <section className="bg-surface-subtle py-12">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="flex flex-col sm:flex-row items-center gap-6 bg-white rounded-xl shadow-card p-6 sm:p-8">
              <div className="shrink-0 flex items-center justify-center h-16 w-16 rounded-xl bg-surface-soft">
                <Compass className="h-8 w-8 text-accent" />
              </div>
              <div className="text-center sm:text-left flex-1">
                <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-1">Authorised partner</p>
                <h3 className="font-heading font-bold text-primary text-xl mb-1">
                  Booking through Tripsyfy = booking through Quark
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We are an authorised booking partner of Quark Expeditions®. You receive the same
                  price, the same ships, and the same expert team — plus our dedicated travel
                  advisors handle every detail from start to finish.
                </p>
              </div>
              <div className="shrink-0">
                <Button
                  size="lg"
                  className="bg-accent text-white hover:bg-accent/90 whitespace-nowrap gap-2"
                  onClick={() => {
                    setQuoteData({ type: "cruise", destination: "Antarctica", cruiseName: "Antarctica Expedition" });
                    setQuoteOpen(true);
                  }}
                >
                  Start Planning <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />

      <QuoteRequestDialog
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        quoteData={quoteData}
      />
    </div>
  );
}
